import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useBetterAuthConvexAuth } from '../../hooks/useAppAuth';

interface SecretPopoutProps {
  sessionId: string;
}

const SecretPopout: React.FC<SecretPopoutProps> = ({ sessionId }) => {
  const { convexUser } = useBetterAuthConvexAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    mobile_number: '',
    telephone_number: '',
    update_details: false
  });

  // Convex mutations
  const recordAction = useMutation(api.analytics.recordPopoutAction);
  const submitInterest = useMutation(api.analytics.submitInterest);

  // Check if already submitted using the authenticated user or email.
  const submissionCheck = useQuery(
    api.analytics.checkInterestSubmission,
    convexUser?.email
      ? {
          email: convexUser.email,
        }
      : {}
  );

  const alreadySubmitted = submissionCheck?.hasSubmitted || false;

  // Track view on mount
  useEffect(() => {
    trackInteraction('view');
  }, []);

  // Populate form with user details if authenticated
  useEffect(() => {
    if (convexUser) {
      setFormData(prev => ({
        ...prev,
        email: convexUser.email || '',
        name: convexUser.name || '',
        mobile_number: convexUser.mobileNumber || '',
        telephone_number: convexUser.telephoneNumber || ''
      }));
    }
  }, [convexUser]);

  const trackInteraction = async (action: string) => {
    try {
      await recordAction({
        sessionId,
        action,
      });
    } catch (error) {
      console.error('Failed to track interaction:', error);
    }
  };

  const handleTabClick = () => {
    setIsOpen(true);
    trackInteraction('click_tab');
  };

  const handleClose = () => {
    setIsOpen(false);
    setShowForm(false);
    setSuccess(false);
    setError('');
    trackInteraction('close');
  };

  const handleInterestedClick = () => {
    setShowForm(true);
    trackInteraction('open');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await trackInteraction('submit');
      const result = await submitInterest({
        email: formData.email,
        name: formData.name || undefined,
        mobileNumber: formData.mobile_number || undefined,
        telephoneNumber: formData.telephone_number || undefined,
        updateUserDetails: formData.update_details,
        source: 'popout',
      });

      if (result.success) {
        setSuccess(true);
        setShowForm(false);
      } else {
        setError('Failed to submit form. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Side Tab */}
      <div
        onClick={handleTabClick}
        className={`fixed left-0 top-1/2 -translate-y-1/2 bg-purple-600 text-white px-2 py-4 rounded-r-lg cursor-pointer shadow-lg hover:bg-purple-700 transition-all z-40 ${
          isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{ writingMode: 'vertical-rl' }}
      >
        <span className="text-sm font-medium font-sans tracking-wider">We have a secret!</span>
      </div>

      {/* Popout Panel */}
      <div
        className={`fixed left-0 top-1/2 -translate-y-1/2 bg-white rounded-r-xl shadow-2xl transition-all duration-300 z-50 max-h-[90vh] overflow-y-auto ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ width: '400px', maxWidth: '90vw' }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-6">
          {/* Header with Image */}
          <div className="text-center mb-6">
            <img
              src="/secret.jpg"
              alt="Secret"
              className="w-32 h-32 object-cover rounded-full mx-auto mb-4 shadow-lg"
            />
            <h2 className="text-2xl font-bold text-purple-600 mb-2">The secret your utility company doesn't want you to know</h2>
            <p className="text-gray-700">Something big is coming to solar. Be the first to take advantage.</p>
          </div>

          {alreadySubmitted ? (
            <div className="text-center">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                <svg className="w-16 h-16 text-purple-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xl font-bold text-purple-800 mb-2">You're already in the know!</p>
                <p className="text-purple-700">We've got your details and will be in touch soon with our exciting news.</p>
              </div>
            </div>
          ) : !showForm && !success ? (
            <div className="text-center">
              <p className="text-lg font-medium font-sans mb-4">Interested?</p>
              <button
                onClick={handleInterestedClick}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium font-sans"
              >
                Sign up to learn more
              </button>
            </div>
          ) : success ? (
            <div className="text-center">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <svg className="w-12 h-12 text-green-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-green-800 font-medium font-sans">Thank you for your interest!</p>
                <p className="text-green-700 text-sm mt-2">We'll be in touch soon with exciting news.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telephone Number
                </label>
                <input
                  type="tel"
                  value={formData.telephone_number}
                  onChange={(e) => setFormData({ ...formData, telephone_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {convexUser && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="update_details"
                    checked={formData.update_details}
                    onChange={(e) => setFormData({ ...formData, update_details: e.target.checked })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label htmlFor="update_details" className="ml-2 text-sm text-gray-700">
                    Update my account details with this information
                  </label>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={handleClose}
        />
      )}
    </>
  );
};

export default SecretPopout;
